import {
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Button,
  FormErrorMessage,
  Text,
  InputRightElement,
  InputGroup,
  Checkbox,
  Flex,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { GoogleSocialSignInButton } from '@/components/ui/shared/social/SocialSignInButton/SocialSignInButton';
import { useAnalytics } from '@/context/Analytics';
import { AccessFrom } from '@/enums/AccessFrom';
import { IToastType } from '@/enums/ToastType';
import { useLoginMutation } from '@/hooks/useLoginMutation';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useToast } from '@/hooks/useToast';
import { ILoginData } from '@/resources/upgrade/Session';

interface ILoginProps {
  password: string;
  email: string;
  remember_me: boolean;
}

interface ILibrariansContactFormProps {
  onClose: () => void;
  setIsForgotOpen: Dispatch<SetStateAction<boolean>>;
}

export const LoginModal: FC<ILibrariansContactFormProps> = ({ onClose, setIsForgotOpen }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const queryClient = useQueryClient();

  const router = useRouter();

  const analytics = useAnalytics();

  const { accessFrom = AccessFrom.Core } = useQueryParams<{ accessFrom: AccessFrom }>({
    accessFrom: StringParam,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ILoginProps>();

  const [logIn] = useLoginMutation({
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['sessions', 'me'] });
      onClose();

      if (session?.user) {
        analytics.identify(session.user.id, {
          firstName: session.user.first_name,
          lastName: session.user.last_name,
          email: session.user.email,
          createdAt: format(new Date(), 'yyyy-M-dd'),
        });
      }

      analytics.track('user_login');

      toast({ status: IToastType.SUCCESS, title: t('loginPopup.notifications.successfullyLoggedIn') });
    },
    onFailure: (error) => {
      const axiosError = error as AxiosError<{ errors?: Array<{ detail: string }> }>;
      const apiErrors = axiosError.response?.data?.errors;

      if (apiErrors?.length) {
        const errorMessages = apiErrors.map((e) => e.detail).join('');

        toast({ status: IToastType.ERROR, title: errorMessages });
      } else {
        toast({ status: IToastType.ERROR, title: error.message });
      }
    },
  });

  const onSubmit = async (formData: ILoginProps) => {
    const data: ILoginData = {
      data: {
        type: 'sessions',
        id: '',
        attributes: {
          ...formData,
        },
      },
    };

    logIn(data);
  };

  function handleCreateAccountClick() {
    analytics.track('user_account_create_click', { location: 'log_in' });
    router.push(`/create-account?accessFrom=${accessFrom}`);
  }

  return (
    <ModalContent px={{ base: 0, sm: 4, lg: 18 }} py={{ base: 10, md: 14 }}>
      <ModalCloseButton fontSize={{ base: 'md', lg: 'xl' }} m={4} />
      <ModalHeader fontSize={{ base: '2xl', lg: '3xl' }} lineHeight={9} fontWeight="bold" mb={2}>
        {t('loginPopup.loginModal.heading')}
      </ModalHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <Text mb="10">{t('loginPopup.loginModal.description')}</Text>
          <Flex flexDirection="column">
            <FormControl mb={8} variant="floating" isInvalid={Boolean(errors.email)}>
              <Input
                placeholder=" "
                {...register('email', {
                  required: t('form.required'),
                })}
                variant="flushed"
                errorBorderColor="none"
                autoComplete="email"
                _invalid={{ boxShadow: 'none' }}
                _autofill={{
                  transition: 'background-color 5000s ease-in-out 0s',
                }}
              />
              <FormLabel>{t('loginPopup.loginModal.email')}</FormLabel>
              <FormErrorMessage>{t('form.required')}</FormErrorMessage>
            </FormControl>

            <FormControl mb={8} variant="floating" isInvalid={Boolean(errors.password)}>
              <InputGroup size="md">
                <Input
                  placeholder=" "
                  {...register('password', {
                    required: t('form.required'),
                  })}
                  type={showPassword ? 'text' : 'password'}
                  variant="flushed"
                  errorBorderColor="none"
                  autoComplete="current-password"
                  _invalid={{ boxShadow: 'none' }}
                  _autofill={{
                    transition: 'background-color 5000s ease-in-out 0s',
                  }}
                />
                <FormLabel mb={8}>{t('loginPopup.loginModal.password')}</FormLabel>
                <InputRightElement minW="max-content">
                  <Button variant="unstyled" onClick={() => setShowPassword((sp) => !sp)}>
                    {showPassword ? t('loginPopup.loginModal.hidePassword') : t('loginPopup.loginModal.showPassword')}
                  </Button>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{t('form.required')}</FormErrorMessage>
            </FormControl>

            <Checkbox colorScheme="violet" size="md" {...register('remember_me')}>
              {t('loginPopup.loginModal.rememberMe')}
            </Checkbox>

            <Button w="full" type="submit" mt={8} mb={4}>
              {t('loginPopup.loginModal.logIn')}
            </Button>

            <Button mb={4} h="8" variant="unstyled" alignSelf="center" onClick={() => setIsForgotOpen(true)}>
              {t('loginPopup.loginModal.forgotPassword')}
            </Button>

            <GoogleSocialSignInButton actionType="login">{t('googleLogin')}</GoogleSocialSignInButton>

            <Flex w="full" mt={12} justifyContent="center" mb={2} alignItems="center">
              <Text as="span" pt="1" mr={2}>
                {t('loginPopup.loginModal.dontHaveAccount')}{' '}
              </Text>
              <Button h="6" variant="unstyled" onClick={handleCreateAccountClick}>
                {t('loginPopup.loginModal.createAccount')}
              </Button>
            </Flex>
          </Flex>
        </ModalBody>
      </form>
    </ModalContent>
  );
};
